import React, { useEffect, useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Theme } from '@mui/material';
import { EditorTheme, GoogleFontSettings } from '@loyalty/shared-common/template-api-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { GridSize } from '@mui/material/Grid/Grid';
import { colorPalette } from '@qualifio/ui-commons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ColorSample } from '../../../../shared/color-sample';
import { FieldList } from './FieldList';
import { FontsLoader } from './FontsLoader';
import { useFonts } from '../../../hooks';
import { useJoiResolver } from '../../../../shared/forms/use-joi-resolver';
import { validationSchema } from './validation-schema';

interface Props {
  values?: Partial<EditorTheme>;
  onChange?: (event: any) => void;
}

const styles = ({ spacing }: Theme) => ({
  root: {
    padding: spacing(0),
    zIndex: 0,
  },
  expandIcon: {
    fontSize: 12,
  },
  adornmentText: {
    color: colorPalette.grey.main,
  },
});

const useStyles = makeStyles(styles);

const StyledAccordion = styled(Accordion)(() => ({
  display: 'table',
  border: 'none !important',
  borderRadius: 0,
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  backgroundColor: 'white',
  fontSize: 16,
  color: '#12384a',
  svg: {
    color: '#12384a !important',
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
  borderRadius: 0,
  border: 'none',
  padding: 10,
}));

const WEIGHT_OPTIONS = [
  { value: '200', text: 'Light' },
  { value: '400', text: 'Normal' },
  { value: '600', text: 'Medium' },
  { value: '800', text: 'Bold' },
];

export function LayoutPanel({ values, onChange }: Props): React.ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();

  // Use React Query hook for fonts (auto-fetches)
  const { data: fonts = [] } = useFonts();

  const resolver = useJoiResolver(validationSchema);
  const form = useForm<EditorTheme>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: values,
    resolver,
  });

  const { handleSubmit, reset } = form;

  useEffect(() => reset(values), [values]);

  const programFonts = useMemo(
    () =>
      fonts.map(font => ({
        id: font.id,
        value: font.name,
        text: font.name,
        family:
          font.type === 'google'
            ? (font.settings as GoogleFontSettings).cssRule
                .replace('font-family: ', '')
                .replace(';', '')
            : font.name,
      })),
    [fonts],
  );

  const globalFields = useMemo(
    () => [
      {
        label: t('editor.layout.global.backgroundImage'),
        name: 'global.backgroundImage',
        type: 'backgroundImage',
      },
      {
        label: t('editor.layout.global.backgroundColor'),
        name: 'global.backgroundColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.global.primaryColor'),
        name: 'global.primaryColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.global.secondaryColor'),
        name: 'global.secondaryColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.global.blocksColor'),
        name: 'global.blocksColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.global.globalRadius'),
        name: 'global.globalRadius',
        type: 'number',
        endAdornment: () => <span className={classes.adornmentText}>px</span>,
      },
      {
        label: t('editor.layout.global.imageRadius'),
        name: 'global.imageRadius',
        type: 'number',
        endAdornment: () => <span className={classes.adornmentText}>px</span>,
      },
      {
        label: t('editor.layout.global.favicon'),
        name: 'favicon',
        type: 'image',
      },
    ],
    [classes.adornmentText, t],
  );

  const buttonFields = useMemo(
    () => [
      {
        type: 'select',
        label: t('editor.layout.button.fontFamily'),
        name: 'button.fontFamily',
        options: programFonts,
        defaultValue: programFonts[0].value,
      },
      {
        label: t('editor.layout.button.fontSize'),
        name: 'button.fontSize',
        type: 'number',
        endAdornment: () => <span className={classes.adornmentText}>px</span>,
      },
      {
        type: 'select',
        label: t('editor.layout.button.fontWeight'),
        name: 'button.fontWeight',
        options: WEIGHT_OPTIONS,
        defaultValue: WEIGHT_OPTIONS[0].value,
      },
      {
        label: t('editor.layout.button.color'),
        name: 'button.color',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.button.backgroundColor'),
        name: 'button.backgroundColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.button.borderRadius'),
        name: 'button.borderRadius',
        type: 'number',
        endAdornment: () => <span className={classes.adornmentText}>px</span>,
      },
    ],
    [classes.adornmentText, t, programFonts],
  );

  const baseTextFields = useMemo(
    () => [
      {
        type: 'select',
        name: 'fontFamily',
        options: programFonts,
        defaultValue: programFonts[0].value,
        size: 6 as GridSize,
      },
      {
        type: 'select',
        name: 'fontWeight',
        options: WEIGHT_OPTIONS,
        defaultValue: WEIGHT_OPTIONS[0].value,
        size: 6 as GridSize,
      },
      {
        name: 'color',
        endAdornment: (color?: string) => <ColorSample color={color} />,
        size: 6 as GridSize,
      },
      {
        name: 'fontSize',
        type: 'number',
        endAdornment: () => <span className={classes.adornmentText}>px</span>,
        size: 6 as GridSize,
      },
    ],
    [classes.adornmentText, programFonts],
  );

  const paragraphFields = useMemo(
    () => [
      ...baseTextFields,
      {
        label: t('editor.layout.text.linkColor'),
        name: 'linkColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
    ],
    [baseTextFields, t],
  );

  const progressBarFields = useMemo(
    () => [
      {
        label: t('editor.layout.progressBar.backgroundColor'),
        name: 'backgroundColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
      {
        label: t('editor.layout.progressBar.progressColor'),
        name: 'progressColor',
        endAdornment: (color?: string) => <ColorSample color={color} />,
      },
    ],
    [t],
  );

  const handleSave = (values: EditorTheme) => {
    onChange?.(values);
  };

  return (
    <FormProvider {...form}>
      <FontsLoader fonts={fonts} />
      <form className={classes.root} onSubmit={handleSubmit(handleSave)}>
        <StyledAccordion>
          <StyledAccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            {t('editor.layout.global.title')}
          </StyledAccordionSummary>
          <StyledAccordionDetails sx={{ borderRadius: 0, p: 1 }}>
            <FieldList title="Theme" fields={globalFields} onApply={handleSubmit(handleSave)} />
          </StyledAccordionDetails>
        </StyledAccordion>
        <StyledAccordion>
          <StyledAccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            {t('editor.layout.text.title')}
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <FieldList
              title={t('editor.layout.paragraph.title')}
              prefix="text.paragraph"
              fields={paragraphFields}
              onApply={handleSubmit(handleSave)}
            />
          </StyledAccordionDetails>
          <StyledAccordionDetails>
            <FieldList
              title={t('editor.layout.title.title')}
              prefix="text.title"
              fields={baseTextFields}
              onApply={handleSubmit(handleSave)}
            />
          </StyledAccordionDetails>
          <StyledAccordionDetails>
            <FieldList
              title={t('editor.layout.subtitle.title')}
              prefix="text.subtitle"
              fields={baseTextFields}
              onApply={handleSubmit(handleSave)}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
        <StyledAccordion>
          <StyledAccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            {t('editor.layout.button.title')}
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <FieldList fields={buttonFields} onApply={handleSubmit(handleSave)} />
          </StyledAccordionDetails>
        </StyledAccordion>
        <StyledAccordion>
          <StyledAccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            {t('editor.layout.progressBar.title')}
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <FieldList
              prefix="progressBar"
              fields={progressBarFields}
              onApply={handleSubmit(handleSave)}
            />
          </StyledAccordionDetails>
        </StyledAccordion>
      </form>
    </FormProvider>
  );
}
